import PendingReservationsBadge from "@/components/PendingReservationsCounter/PendingReservationsBadge";
import { zIndex } from "@/constants";
import useDevice from "@/hooks/useDevice";
import useUser from "@/hooks/useUser";
import icons from "@/tools/icons";
import { Badge, Stack, useTheme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { ComponentType, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoWithEnvironment from "../../LogoWithEnvironment";
import useVenuePortalUserOptions from "../../VenuePortalUserOptionsAutocomplete/useVenuePortalUserOptions";
import CalendarDrawer from "./components/CalendarDrawer";
import DrawerButton, { DrawerProps } from "./components/DrawerButton";
import ImpersonationDrawer from "./components/ImpersonationDrawer";
import NavigationDrawer from "./components/NavigationDrawer";
import { NotificationsDrawer } from "./components/NotificationsDrawer";
import SearchDrawer from "./components/SearchDrawer";

type DrawerType =
  | "navigation"
  | "search"
  | "impersonation"
  | "notifications"
  | "calendar";
export type MobileDrawerProps = DrawerProps;

export default function MobileHeader() {
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState<null | DrawerType>(null);
  const closeDrawer = useCallback(() => setOpenDrawer(null), []);
  const { user, isMtUser } = useUser();
  const { isMobile } = useDevice();

  const { venuePortalUserOptionMap } = useVenuePortalUserOptions();

  const impersonatedUser = venuePortalUserOptionMap.get(user.userId ?? "");
  const firstLetterOfEmail = impersonatedUser?.label?.[0] ?? null;

  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";

  const { notificationLinks, notificationsLoading, numUnreadNotifications } =
    useUser();

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: isLightMode ? "primary" : "background.paper",
        boxShadow: 0,
        flexShrink: 0,
        zIndex: zIndex.high,
      }}
      enableColorOnDark={true}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Stack
          direction="row"
          gap={1}
          sx={{ cursor: "pointer" }}
          onClick={() => {
            closeDrawer();
            navigate("/");
          }}
          alignItems="center"
        >
          <LogoWithEnvironment />
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {[
            {
              name: "calendar",
              Icon: <icons.Calendar sx={{ color: "#FFFFFF" }} />,
              Drawer: CalendarDrawer,
              hide: !isMobile,
            },
            {
              name: "impersonation",
              Icon: (
                <Badge badgeContent={firstLetterOfEmail} color="primary">
                  <icons.Fingerprint sx={{ color: "#FFFFFF" }} />
                </Badge>
              ),
              Drawer: ImpersonationDrawer,
              hide: !isMtUser,
            },
            {
              name: "search",
              Icon: <icons.Search sx={{ color: "#FFFFFF" }} />,
              Drawer: SearchDrawer,
            },
            {
              name: "notifications",
              Icon: (
                <Badge color="secondary" badgeContent={numUnreadNotifications}>
                  <icons.Notifications sx={{ color: "#FFFFFF" }} />
                </Badge>
              ),
              Drawer: NotificationsDrawer,
              drawerProps: {
                notificationLinks,
                notificationsLoading,
              },
            },
            {
              name: "navigation",
              Icon: (
                <PendingReservationsBadge>
                  <icons.Menu sx={{ color: "#FFFFFF" }} />
                </PendingReservationsBadge>
              ),
              Drawer: NavigationDrawer,
              drawerProps: {},
            },
          ]
            .filter(({ hide }) => !hide)
            .map(({ name, Icon, Drawer, drawerProps }) => (
              <DrawerButton
                key={name}
                open={openDrawer === name}
                handleClose={closeDrawer}
                handleOpen={() => setOpenDrawer(name as DrawerType)}
                Icon={Icon}
                Drawer={Drawer as ComponentType<DrawerProps>}
                drawerProps={drawerProps}
              />
            ))}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
